import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { Table } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PAGE_SIZE_OPTIONS } from '@/shared/api/api-types';
import { formatNumber } from '@/shared/lib/format';

/**
 * Pagination controls for a server-driven table.
 *
 * Shows the absolute row range rather than just a page number, because
 * "1–25 of 3,184" answers the question operators actually have — how much is
 * there — which a bare page count does not.
 */
export function DataTablePagination<TData>({ table }: { table: Table<TData> }) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = table.getRowCount();
  const pageCount = table.getPageCount();

  const firstRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <div className="flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
      <p className="text-muted-foreground text-xs">
        {totalRows === 0 ? (
          'No results'
        ) : (
          <>
            <span className="text-foreground font-medium tabular-nums">
              {formatNumber(firstRow)}–{formatNumber(lastRow)}
            </span>{' '}
            of{' '}
            <span className="text-foreground font-medium tabular-nums">
              {formatNumber(totalRows)}
            </span>
          </>
        )}
      </p>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Rows</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger size="sm" className="h-7 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-muted-foreground me-1 text-xs tabular-nums">
            Page {formatNumber(pageIndex + 1)} of {formatNumber(Math.max(pageCount, 1))}
          </span>

          {/* Icons are mirrored under RTL so "previous" always points back. */}
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="First page"
          >
            <ChevronsLeft className="size-3.5 rtl:rotate-180" aria-hidden />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-3.5 rtl:rotate-180" aria-hidden />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
          >
            <ChevronRight className="size-3.5 rtl:rotate-180" aria-hidden />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-7"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Last page"
          >
            <ChevronsRight className="size-3.5 rtl:rotate-180" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
